import React, { useState, useEffect } from 'react';
import * as M from '@material-ui/core';
import { Add, Delete, Edit, Search, Clear } from '@material-ui/icons';
import { api } from '@services/api';
import { ToastMessage } from '@components/ToastMessage';
import { type Color } from '@material-ui/lab';

interface Servico {
  id_servicos: number;  // ← API retorna número, não string
  nome: string;         // ← API retorna "nome"
}

export function Servicos() {
  const [servicos, setServicos] = useState<Servico[]>([]);
  const [servicosFiltrados, setServicosFiltrados] = useState<Servico[]>([]);
  const [codigo, setCodigo] = useState('');
  const [nome, setNome] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [messageSend, setMessageSend] = useState({
    cod: '',
    message: '',
    severity: '' as Color,
    show: false
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [servicoToDelete, setServicoToDelete] = useState<Servico | null>(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [servicoToEdit, setServicoToEdit] = useState<Servico | null>(null);
  const [editCodigo, setEditCodigo] = useState('');
  const [editNome, setEditNome] = useState('');

  useEffect(() => {
    fetchServicos();
  }, []);

  const filtrarServicos = (termo: string) => {
    if (!termo.trim()) {
      setServicosFiltrados(servicos);
      setPage(0);
      return;
    }

    const termoLowerCase = termo.toLowerCase().trim();
    const filtrados = servicos.filter(
      (servico) =>
        String(servico.id_servicos).toLowerCase().includes(termoLowerCase) ||
        servico.nome.toLowerCase().includes(termoLowerCase)
    );
    setServicosFiltrados(filtrados);
    setPage(0);
  };

  const handleSearch = () => {
    filtrarServicos(searchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setServicosFiltrados(servicos);
    setPage(0);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  async function fetchServicos() {
    setIsLoading(true);
    try {
      const res = await api.get('/ravim/servicos');
      if (res.status === 200) {
        setServicos(res.data);
        setServicosFiltrados(res.data);
      }
    } catch (error: any) {
      setMessageSend({
        cod: error.code,
        message: error.message,
        severity: 'error',
        show: true
      });
    } finally {
      setIsLoading(false);
    }
  }

  async function addServico() {
    if (!codigo.trim() || !nome.trim()) {
      setMessageSend({
        cod: '400',
        message: 'Preencha todos os campos!',
        severity: 'warning',
        show: true
      });
      return;
    }

    const data = {
      id_servicos: parseInt(codigo),  // ← API espera número
      nome: nome                       // ← API espera "nome"
    };

    setIsLoading(true);
    try {
      const res = await api.post('/ravim/cad-servicos', data);
      if (res.status === 200) {
        setMessageSend({
          cod: String(res.status),
          message: res.data.message,
          severity: 'success',
          show: true
        });
        setCodigo('');
        setNome('');
        fetchServicos();
      }
    } catch (error: any) {
      setMessageSend({
        cod: error.code,
        message: error.response?.data?.message || error.message,
        severity: 'error',
        show: true
      });
    } finally {
      setIsLoading(false);
    }
  }

  const handleDeleteClick = (servico: Servico) => {
    setServicoToDelete(servico);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setServicoToDelete(null);
  };

  async function confirmDeleteServico() {
    if (servicoToDelete) {
      setIsLoading(true);
      try {
        const res = await api.delete(`/ravim/servicos/${servicoToDelete.id_servicos}`);
        if (res.status === 200) {
          setMessageSend({
            cod: String(res.status),
            message: res.data.message,
            severity: 'success',
            show: true
          });
          fetchServicos();
        }
      } catch (error: any) {
        setMessageSend({
          cod: error.code,
          message: error.response?.data?.message || error.message,
          severity: 'error',
          show: true
        });
      } finally {
        setIsLoading(false);
        handleCloseDeleteDialog();
      }
    }
  }

  const handleEditClick = (servico: Servico) => {
    setServicoToEdit(servico);
    setEditCodigo(String(servico.id_servicos));
    setEditNome(servico.nome);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setServicoToEdit(null);
    setEditCodigo('');
    setEditNome('');
  };

  async function saveEditedServico() {
    if (servicoToEdit) {
      const data = {
        id_servicos: parseInt(editCodigo),
        nome: editNome
      };
      setIsLoading(true);
      try {
        const res = await api.put(`/ravim/servicos/${servicoToEdit.id_servicos}`, data);
        if (res.status === 200) {
          setMessageSend({
            cod: String(res.status),
            message: res.data.message,
            severity: 'success',
            show: true
          });
          fetchServicos();
        }
      } catch (error: any) {
        setMessageSend({
          cod: error.code,
          message: error.response?.data?.message || error.message,
          severity: 'error',
          show: true
        });
      } finally {
        setIsLoading(false);
        handleCloseEditDialog();
      }
    }
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const dadosExibidos = servicosFiltrados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <M.Container maxWidth="lg" style={{ padding: '20px' }}>
      <M.Box mb={4}>
        <M.Typography
          style={{ textAlign: 'center', fontWeight: 'bold' }}
          variant="h4"
          component="h1"
          color="primary"
        >
          Gerenciamento de Serviços
        </M.Typography>
      </M.Box>

      <M.Grid container spacing={3}>
        {/* Seção de Cadastro */}
        <M.Grid item xs={12} md={4}>
          <M.Paper elevation={3} style={{ padding: '20px', height: '100%' }}>
            <M.Typography variant="h6" gutterBottom>Cadastrar Novo Serviço</M.Typography>
            <M.Box display="flex" flexDirection="column" style={{ gap: 16 }}>
              <M.TextField
                onChange={(e) => setCodigo(e.target.value)}
                label="Código do Serviço"
                variant="outlined"
                value={codigo}
                fullWidth
                size="small"
                type="number"
                placeholder="Ex: 71"
              />
              <M.TextField
                label="Nome do Serviço"
                variant="outlined"
                onChange={(e) => setNome(e.target.value)}
                value={nome}
                fullWidth
                size="small"
                placeholder="Ex: Gestão eSocial - ASO - 2220"
              />
              <M.Button
                startIcon={<Add />}
                onClick={addServico}
                disabled={!(codigo.length > 0 && nome.length > 0)}
                variant="contained"
                color="primary"
                fullWidth
              >
                {isLoading ? 'Cadastrando...' : 'Cadastrar'}
              </M.Button>
            </M.Box>
          </M.Paper>
        </M.Grid>

        {/* Seção de Listagem */}
        <M.Grid item xs={12} md={8}>
          <M.Paper elevation={3} style={{ padding: '20px' }}>
            <M.Typography variant="h6" gutterBottom>Serviços Cadastrados</M.Typography>
            
            {/* Barra de Pesquisa */}
            <M.Box display="flex" alignItems="center" style={{ gap: 2 }} mb={2}>
              <M.TextField
                label="Pesquisar por código ou nome"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyPress={handleKeyPress}
                placeholder="Digite o código ou nome do serviço..."
                style={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <M.InputAdornment position="start">
                      <Search />
                    </M.InputAdornment>
                  ),
                }}
              />
              <M.Button
                onClick={handleSearch}
                variant="contained"
                color="primary"
                startIcon={<Search />}
              >
                Buscar
              </M.Button>
              {searchTerm && (
                <M.Button
                  onClick={handleClearSearch}
                  variant="outlined"
                  color="secondary"
                  startIcon={<Clear />}
                >
                  Limpar
                </M.Button>
              )}
            </M.Box>

            {searchTerm && (
              <M.Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Encontrado(s) {servicosFiltrados.length} resultado(s) para "{searchTerm}"
              </M.Typography>
            )}

            {isLoading ? (
              <M.Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <M.CircularProgress />
              </M.Box>
            ) : servicosFiltrados.length === 0 ? (
              <M.Typography>
                {searchTerm ? 'Nenhum serviço encontrado com o termo pesquisado.' : 'Nenhum serviço cadastrado.'}
              </M.Typography>
            ) : (
              <>
                <M.TableContainer>
                  <M.Table>
                    <M.TableHead>
                      <M.TableRow>
                        <M.TableCell style={{ fontWeight: 'bold' }}>Código</M.TableCell>
                        <M.TableCell style={{ fontWeight: 'bold' }}>Nome do Serviço</M.TableCell>
                        <M.TableCell align="right" style={{ fontWeight: 'bold' }}>Ações</M.TableCell>
                      </M.TableRow>
                    </M.TableHead>
                    <M.TableBody>
                      {dadosExibidos.map((servico) => (
                        <M.TableRow key={servico.id_servicos} hover>
                          <M.TableCell>{servico.id_servicos}</M.TableCell>
                          <M.TableCell>{servico.nome}</M.TableCell>
                          <M.TableCell align="right">
                            <M.IconButton
                              aria-label="editar"
                              onClick={() => handleEditClick(servico)}
                              color="primary"
                              size="small"
                            >
                              <Edit />
                            </M.IconButton>
                            <M.IconButton
                              aria-label="excluir"
                              onClick={() => handleDeleteClick(servico)}
                              color="secondary"
                              size="small"
                            >
                              <Delete />
                            </M.IconButton>
                          </M.TableCell>
                        </M.TableRow>
                      ))}
                    </M.TableBody>
                  </M.Table>
                </M.TableContainer>
                <M.TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={servicosFiltrados.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Linhas por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
              </>
            )}
          </M.Paper>
        </M.Grid>
      </M.Grid>

      {/* Modal de Confirmação de Exclusão */}
      <M.Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <M.DialogTitle>Confirmar Exclusão</M.DialogTitle>
        <M.DialogContent>
          <M.DialogContentText>
            Tem certeza que deseja excluir o serviço "{servicoToDelete?.nome}" (Código: {servicoToDelete?.id_servicos})?
          </M.DialogContentText>
        </M.DialogContent>
        <M.DialogActions>
          <M.Button onClick={handleCloseDeleteDialog} color="primary">
            Cancelar
          </M.Button>
          <M.Button onClick={confirmDeleteServico} color="secondary">
            Excluir
          </M.Button>
        </M.DialogActions>
      </M.Dialog>

      {/* Modal de Edição */}
      <M.Dialog open={openEditDialog} onClose={handleCloseEditDialog}>
        <M.DialogTitle>Editar Serviço</M.DialogTitle>
        <M.DialogContent>
          <M.TextField
            margin="dense"
            label="Código do Serviço"
            type="number"
            fullWidth
            value={editCodigo}
            onChange={(e) => setEditCodigo(e.target.value)}
            disabled
          />
          <M.TextField
            margin="dense"
            label="Nome do Serviço"
            type="text"
            fullWidth
            value={editNome}
            onChange={(e) => setEditNome(e.target.value)}
          />
        </M.DialogContent>
        <M.DialogActions>
          <M.Button onClick={handleCloseEditDialog} color="primary">
            Cancelar
          </M.Button>
          <M.Button onClick={saveEditedServico} color="primary">
            Salvar
          </M.Button>
        </M.DialogActions>
      </M.Dialog>

      <ToastMessage
        code={messageSend.cod}
        message={messageSend.message}
        onClose={() =>
          setMessageSend({
            cod: messageSend.cod,
            message: messageSend.message,
            severity: messageSend.severity,
            show: false
          })
        }
        show={messageSend.show}
        severity={messageSend.severity}
      />
    </M.Container>
  );
}